import { useState } from 'react'
import { Calendar, Clock, X, Loader2, AlertCircle } from 'lucide-react'
import { BridgeRequest, api } from '@/lib/api'
import { shortenAddress } from '@/lib/utils'
import toast from 'react-hot-toast'

interface SchedulePaymentModalProps {
  request: BridgeRequest
  onClose: () => void
  onSuccess: () => void
}

export function SchedulePaymentModal({ request, onClose, onSuccess }: SchedulePaymentModalProps) {
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!date || !time) {
      toast.error('Selecciona fecha y hora')
      return
    }

    const scheduledAt = new Date(`${date}T${time}`)
    if (scheduledAt <= new Date()) {
      toast.error('La fecha debe ser en el futuro')
      return
    }

    setIsSubmitting(true)
    try {
      const result = await api.schedulePayment(
        request.id,
        scheduledAt.toISOString(),
        notes || undefined
      )

      if (result.success) {
        toast.success('Pago programado correctamente')
        onSuccess()
        onClose()
      } else {
        toast.error('Error al programar el pago')
      }
    } catch (error: any) {
      toast.error(error.message || 'Error al programar el pago')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0]

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card p-6 max-w-md w-full space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-400" />
            Programar Pago
          </h3>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-dark-400 hover:text-white disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Request info */}
        <div className="bg-dark-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-dark-400">Solicitud:</span>
            <span className="font-mono">#{request.id.slice(0, 8)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-dark-400">Destinatario:</span>
            <span className="font-mono">{shortenAddress(request.toAddress, 8)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-dark-400">Cantidad:</span>
            <span className="font-mono text-accent-400">
              {parseFloat(request.formattedAmountOut).toFixed(4)} USDT
            </span>
          </div>
        </div>

        {/* Date and time inputs */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-dark-400 mb-1">Fecha</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={today}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-dark-400 mb-1">Hora</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-dark-400 mb-1">Notas (opcional)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas adicionales..."
              className="input w-full"
            />
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-yellow-400">
            El pago se ejecutará automáticamente a la hora indicada usando la wallet Bridge.
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-2">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="btn-secondary btn-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !date || !time}
            className="btn-primary btn-sm bg-purple-500 hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Clock className="w-4 h-4" />
            )}
            Programar Pago
          </button>
        </div>
      </div>
    </div>
  )
}
